import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import { Route, Switch, useHistory } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  DeleteOutlined,
  FileOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Spin } from 'antd';
import { Button, Input, Tooltip } from '@mm/components';

import css from './SetupForm.module.css';
import { useRouteMatch } from 'react-router';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { useSelector } from 'react-redux';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { useGoogleAdsCRUDActions } from '@mm/state/actions/GoogleAdsCRUDActions';
import { selectGoogleClientCredentials } from '@mm/state/selectors/googleAdsSelectors';
import { errorToast } from '../../../../../../common/helpers/toast';
import { useModalActions } from '@mm/state/modules/modalsState/actions';

type SetupFormProps = {
  className?: string;
};

const steps = {
  accounts: {
    path: '',
    number: '1',
    title: 'Accounts',
    description: 'Setup Google Ads',
  },
};

const AccountsStep = () => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const googleAdsCRUDActions = useGoogleAdsCRUDActions();

  const [busy, setBusy] = useState(false);

  const modalActions = useModalActions();

  const credentials = useSelector(selectGoogleClientCredentials);

  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [customerId, setCustomerId] = useState('');

  const handleSave = async () => {
    setBusy(true);

    try {
      const res = await googleAdsCRUDActions.createGoogleClientCredentials(
        facebookPageId,
        {
          clientId,
          clientSecret,
          customerId,
        },
      );

      if (res.success && res?.googleClientCredential?.redirectUrl) {
        window.location.href = res?.googleClientCredential?.redirectUrl;
        return;
      }

      if (res.errors) {
        errorToast(res.errors);
        return;
      }

      googleAdsCRUDActions.retrieveGoogleClientCredentials(facebookPageId);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={css.card}>
        {!credentials?.length ? (
          <>No Saved Accounts</>
        ) : (
          <table className={css.table}>
            <thead>
              <tr>
                <th scope="col">ID</th>
                <th scope="col">Client ID</th>
                <th scope="col">Secret Token</th>
                <th scope="col">Customer ID</th>
                <th scope="col">Refresh Token</th>
                <th scope="col">User</th>
                <th scope="col" />
              </tr>
            </thead>

            <tbody>
              {credentials.map(value => (
                <tr key={value.clientId}>
                  <th scope="row">
                    <div className={css.cell}>{value.id || '-'}</div>
                  </th>
                  <th scope="row">
                    <div className={css.cell}>{value.clientId || '-'}</div>
                  </th>
                  <th scope="row">
                    <div className={css.cell}>{value.clientSecret || '-'}</div>
                  </th>
                  <th scope="row">
                    <div className={css.cell}>{value.customerId || '-'}</div>
                  </th>
                  <th scope="row">
                    <div className={css.cell}>{value.refreshToken || '-'}</div>
                  </th>
                  <th scope="row">
                    <div className={css.cell}>{value.user?.email || '-'}</div>
                  </th>
                  <th scope="row">
                    <div className={css.cell}>
                      <Button
                        color="secondary"
                        onClick={() => {
                          modalActions.openGeneralConfirmModal({
                            headerText: 'Are you sure?',
                            messageText: 'This action can not be undone',
                            onCloseHandler: async ok => {
                              if (ok) {
                                await googleAdsCRUDActions.deleteGoogleClientCredentials(
                                  facebookPageId,
                                  value.id,
                                );
                                googleAdsCRUDActions.retrieveGoogleClientCredentials(
                                  facebookPageId,
                                );
                              }
                            },
                          });
                        }}
                        css={{ container: css.deleteBtn }}
                      >
                        <DeleteOutlined />
                      </Button>
                    </div>
                  </th>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className={css.card}>
        <div className={css.field}>
          <div className={css.fieldName}>
            <span>Client ID</span>
            <Tooltip
              css={{ container: css.tooltip }}
              color="dark"
              position="bottom"
              align="center"
              useHover
              withoutLine
              anchorElement={
                <QuestionCircleOutlined className={css.infoIcon} />
              }
            >
              Learn more about finding your Client ID
              <a
                className={css.learnMoreLink}
                href="https://customers.ai/help/l/en/article/yk3wzsxkpz-finding-your-google-ads-client-id-secret-token-and-customer-id"
                target="_blank"
              >
                Learn more
              </a>
            </Tooltip>
          </div>

          <Input
            onChange={e => setClientId(e.target.value.trim())}
            disabled={busy}
            value={clientId}
            type="text"
            placeholder="Add Client ID"
            css={{ container: css.input }}
          />
        </div>

        <div className={css.field}>
          <div className={css.fieldName}>
            <span>Secret Token</span>
            <Tooltip
              css={{ container: css.tooltip }}
              color="dark"
              position="bottom"
              align="center"
              useHover
              withoutLine
              anchorElement={
                <QuestionCircleOutlined className={css.infoIcon} />
              }
            >
              Learn more about finding your Secret Token
              <a
                className={css.learnMoreLink}
                href="https://customers.ai/help/l/en/article/yk3wzsxkpz-finding-your-google-ads-client-id-secret-token-and-customer-id"
                target="_blank"
              >
                Learn more
              </a>
            </Tooltip>
          </div>

          <Input
            onChange={e => setClientSecret(e.target.value.trim())}
            disabled={busy}
            value={clientSecret}
            placeholder="Add Secret Token"
            type="text"
            css={{ container: css.input }}
          />
        </div>

        <div className={css.field}>
          <div className={css.fieldContainer}>
            <div className={css.fieldName}>
              <span>Customer ID</span>
              <Tooltip
                css={{ container: css.tooltip }}
                color="dark"
                position="bottom"
                align="center"
                useHover
                withoutLine
                anchorElement={
                  <QuestionCircleOutlined className={css.infoIcon} />
                }
              >
                Customer ID
              </Tooltip>
            </div>
            <a
              href=""
              className={classNames(css.whichVersionLink, css.hidden)}
              target="_blank"
            >
              <FileOutlined />
              Which version should I choose?
            </a>
          </div>
          <Input
            onChange={e => setCustomerId(e.target.value.trim())}
            disabled={busy}
            value={customerId}
            type="text"
            placeholder="Add Customer ID"
            css={{ container: css.input }}
          />
        </div>

        <Button
          css={{ container: css.submitBtn }}
          onClick={handleSave}
          disabled={busy || [clientId, clientSecret, customerId].some(v => !v)}
        >
          Save{' '}
          {busy && (
            <Spin
              indicator={
                <LoadingOutlined className={css.submitBtnSpinner} spin />
              }
            />
          )}
        </Button>
      </div>
    </>
  );
};
export const SetupForm = ({ className }: SetupFormProps) => {
  const [configReady, setConfigFetchReady] = useState(false);

  const history = useHistory();
  const match = useRouteMatch<{ step?: string; chatbotId: string }>();

  const credentials = useSelector(selectGoogleClientCredentials);

  const googleAdsCRUDActions = useGoogleAdsCRUDActions();

  const chatbotId = useSelector(activeChatbotIdSelector);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const currentStep = Object.values(steps).findIndex(step =>
    step.path.includes(match.params.step || ''),
  );

  const containerClassName = classNames(className, css.container);

  const isDisabled = useCallback(
    step => {
      switch (step.path) {
        default:
          return false;
      }
    },
    [configReady],
  );
  const isDone = useCallback(
    step => {
      switch (step.path) {
        case steps.accounts.path: {
          return !!credentials.length;
        }
        default:
          return false;
      }
    },
    [configReady],
  );

  const isActive = useCallback(
    step =>
      Object.values(steps).findIndex(v => v.path === step.path) === currentStep,
    [configReady, currentStep],
  );

  useEffect(() => {
    (async () => {
      try {
        await googleAdsCRUDActions.retrieveGoogleClientCredentials(
          facebookPageId,
        );
      } finally {
        setConfigFetchReady(true);
      }
    })();
  }, [chatbotId]);

  return (
    <div className={containerClassName}>
      <div className={css.sidebar}>
        <Button
          css={{ container: css.backBtn }}
          color="transparent"
          onClick={() => {
            if (history?.location?.state?.['from']) {
              history.push(
                BotBuilderRoutes.integrations.getLink({ chatbotId }),
                { kind: 'google_ads' },
              );
            } else {
              history.push(BotBuilderRoutes.restore.getLink({ chatbotId }), {
                options: 'google_ads_audience',
              });
            }
          }}
        >
          <ArrowLeftOutlined />
          Back to{' '}
          {history?.location?.state?.['from'] === 'integrations'
            ? 'Integrations'
            : 'Restore'}
        </Button>

        <span className={css.sidebarTitle}>Google Ads Setup</span>
        <span className={css.sidebarSubtitle}>Send data to Google ads...</span>

        <ul
          className={classNames(css.stepMenu, {
            [css.disabled]: !configReady,
          })}
        >
          {Object.values(steps).map((step, idx) => (
            <li
              key={step.title}
              className={classNames(css.stepMenuItem, {
                [css.disabled]: isDisabled(step),
                [css.done]: isDone(step),
                [css.active]: isActive(step),
              })}
              onClick={
                isDisabled(step)
                  ? undefined
                  : () => {
                      history.push(
                        match.path
                          .replace(':chatbotId', match.params.chatbotId)
                          .replace(':step?', step.path.replace('/', '')),
                      );
                    }
              }
            >
              <span className={css.stepNumber}>
                {isDone(step) ? <CheckOutlined /> : step.number}
              </span>
              <span className={css.stepTitle}>{step.title}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className={css.stepContent}>
        <div className={css.stepContentHeader}>
          <div className={css.stepContentHeaderText}>
            <span className={css.stepContentTitle}>
              {Object.values(steps)[currentStep].title}
            </span>
            <span className={css.stepContentDescription}>
              {Object.values(steps)[currentStep].description}
            </span>
          </div>
          <div className={css.stepActions}>
            <a
              href="https://customers.ai/help/l/en/article/yk3wzsxkpz-finding-your-google-ads-client-id-secret-token-and-customer-id"
              target="_blank"
              className={css.howToLink}
              rel="noreferrer"
            >
              How does Google Ads work?
            </a>
          </div>
        </div>

        {configReady && (
          <Switch>
            <Route
              path={match.path + steps.accounts.path}
              component={AccountsStep}
            />
          </Switch>
        )}
      </div>
    </div>
  );
};
